/**
 * POST a multipart form through XMLHttpRequest instead of `fetch`.
 *
 * Since Expo SDK 56 the global `fetch` is `expo/fetch`, which refuses React
 * Native's `{ uri, name, type }` FormData parts ("Unsupported FormDataPart
 * implementation"). XMLHttpRequest still accepts them and streams the file from
 * disk into the multipart body, exactly as before: React Native's old `fetch`
 * was a polyfill on top of XMLHttpRequest. No `Content-Type` header is set, so
 * the request carries its own multipart boundary.
 *
 * Returns the parts of a fetch Response that callers read: `ok`, `status`,
 * `json()` and `text()`.
 */
export interface FormResponse {
    ok: boolean;
    status: number;
    text(): Promise<string>;
    json(): Promise<any>; // eslint-disable-line @typescript-eslint/no-explicit-any
}

export function postForm(
    url: string,
    headers: Record<string, string>,
    body: FormData
): Promise<FormResponse> {
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("POST", url);
        for (const [name, value] of Object.entries(headers)) {
            xhr.setRequestHeader(name, value);
        }
        xhr.onload = () => {
            const raw = typeof xhr.responseText === "string" ? xhr.responseText : "";
            resolve({
                ok: xhr.status >= 200 && xhr.status < 300,
                status: xhr.status,
                text: async () => raw,
                json: async () => JSON.parse(raw)
            });
        };
        // Same rejection shape as fetch: a TypeError for a network failure.
        xhr.onerror = () => reject(new TypeError("Network request failed"));
        xhr.ontimeout = () => reject(new TypeError("Network request timed out"));
        xhr.send(body);
    });
}
